/**
 * California counties + optional cities: choropleth of per-capita GHG from ghg_inventory_precalculated + population.
 * Counties: `public/data/california_counties.geojson` (interior rings stripped at load—avoids lake/reservoir white outlines).
 * Cities: `public/data/california_places.geojson` (CDPs filtered; holes stripped).
 * Quintile **breakpoints** are fixed from `PUBLICATION_MAP_QUINTILE_REFERENCE_YEAR` (2023: combined counties + cities);
 * the **Year** control only changes emissions/population values mapped onto that scale (e.g. higher 2006 per capita → more red).
 */
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import React, { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { Feature, FeatureCollection } from 'geojson';
import type { GeoJSONOptions } from 'leaflet';
import { GeoJSON, MapContainer, TileLayer, useMap } from 'react-leaflet';
import {
  PUBLICATION_GHG_CHART_YEAR_MIN,
  PUBLICATION_GHG_SPATIAL_YEAR_MAX,
  PUBLICATION_MAP_QUINTILE_REFERENCE_YEAR,
} from '../../config/publicationGhgChartYears';
import { PUBLICATION_ANALYSIS_CHART_IDS } from '../../config/publicationAnalysisChartIds';
import { ChartPngPageIdOverrideContext, useChartPngRegistryActions } from '../../contexts/ChartPngRegistryContext';
import {
  choroplethValueForMap,
  COUNTY_GHG_MAP_METRIC_OPTIONS,
  fetchCaliforniaCountyCityGhgForMap,
  perCapitaForMetric,
  sectorMtForMetric,
  type CountyGhgMapDisplayMode,
  type CountyGhgMapMetric,
  type CountyGhgMapRow,
} from '../../services/countyPerCapitaGhgMapService';
import { inventoryGeofipsNumeric } from '../../services/ghgInventoryGeofipsCanonical';
import {
  CHOROPLETH_NO_DATA_FILL,
  CHOROPLETH_QUINTILE_COLORS,
  getQuintileColor,
  getQuintileIndex,
  getQuintileThresholds,
} from '../../utils/choroplethQuintiles';
import { stripPolygonHolesFromFeatureCollection } from '../../utils/geojsonStripPolygonHoles';
import { isUsableLatLngBounds } from '../../utils/leafletBounds';
import { FitMapBounds } from '../maps/FitMapBounds';
import { downloadDataAsCSV, downloadDomAsPNG, formatFilename } from '../../utils/downloadUtils';
import { PublicationFigureDownloads } from '../publication/PublicationFigureDownloads';

delete (L.Icon.Default.prototype as unknown as { _getIconUrl?: string })._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const COUNTY_GEOJSON_URL = `${process.env.PUBLIC_URL || ''}/data/california_counties.geojson`;
const PLACE_GEOJSON_URL = `${process.env.PUBLIC_URL || ''}/data/california_places.geojson`;

const CALIFORNIA_MAP_BOUNDS = L.latLngBounds(
  [32.53, -124.48],
  [42.01, -114.13]
);
const CALIFORNIA_MAP_CENTER: [number, number] = [37.25, -119.5];
const CALIFORNIA_MAP_ZOOM = 6;
const CALIFORNIA_MAP_FIT_OPTIONS: L.FitBoundsOptions = { padding: [10, 10] };

const PANE_COUNTIES = 'cpCounties';
const PANE_CITIES = 'cpCities';

/** Below this zoom, city polygons omit the white outline (state view stays uncluttered). */
const CITY_CHOROPLETH_OUTLINE_MIN_ZOOM = 9;

type MapSyncController = {
  register: (id: string, map: L.Map) => () => void;
  syncFrom: (sourceId: string) => void;
  adoptPeerView: (targetId: string) => void;
};

function useMapSyncController(): MapSyncController {
  const mapsRef = useRef(new Map<string, L.Map>());
  const syncingRef = useRef(false);

  const register = useCallback((id: string, map: L.Map) => {
    mapsRef.current.set(id, map);
    return () => {
      mapsRef.current.delete(id);
    };
  }, []);

  const syncFrom = useCallback((sourceId: string) => {
    if (syncingRef.current) return;
    const source = mapsRef.current.get(sourceId);
    if (!source) return;
    syncingRef.current = true;
    try {
      const center = source.getCenter();
      const zoom = source.getZoom();
      mapsRef.current.forEach((map, id) => {
        if (id === sourceId) return;
        if (map.getZoom() === zoom && map.getCenter().distanceTo(center) < 1) return;
        map.setView(center, zoom, { animate: false });
      });
    } finally {
      syncingRef.current = false;
    }
  }, []);

  const adoptPeerView = useCallback((targetId: string) => {
    const target = mapsRef.current.get(targetId);
    if (!target) return;
    for (const [id, map] of Array.from(mapsRef.current.entries())) {
      if (id !== targetId) {
        target.setView(map.getCenter(), map.getZoom(), { animate: false });
        return;
      }
    }
  }, []);

  return useMemo(
    () => ({ register, syncFrom, adoptPeerView }),
    [register, syncFrom, adoptPeerView]
  );
}

/** Keep paired Analysis maps on the same center/zoom. */
function LinkedMapView({ mapId, sync }: { mapId: string; sync: MapSyncController }) {
  const map = useMap();
  useEffect(() => {
    const unregister = sync.register(mapId, map);
    const syncHandler = () => sync.syncFrom(mapId);
    map.whenReady(() => sync.adoptPeerView(mapId));
    map.on('move', syncHandler);
    map.on('zoom', syncHandler);
    return () => {
      map.off('move', syncHandler);
      map.off('zoom', syncHandler);
      unregister();
    };
  }, [map, mapId, sync]);
  return null;
}

type MapPanelSnapshot = {
  panelLabel: string;
  year: number;
  metric: CountyGhgMapMetric;
  displayMode: CountyGhgMapDisplayMode;
  countyRows: CountyGhgMapRow[];
  cityRows: CountyGhgMapRow[];
};

function csvRecordsFromMapSnapshot(snap: MapPanelSnapshot | null): Record<string, unknown>[] {
  if (!snap) return [];
  const metricLabel =
    COUNTY_GHG_MAP_METRIC_OPTIONS.find((o) => o.value === snap.metric)?.label ?? snap.metric;
  const rows: Record<string, unknown>[] = [];
  for (const r of snap.countyRows) {
    const pc = perCapitaForMetric(r, snap.metric);
    const sm = sectorMtForMetric(r, snap.metric);
    const mv = choroplethValueForMap(r, snap.metric, snap.displayMode);
    rows.push({
      map_panel: snap.panelLabel,
      geography_type: 'County',
      geofips: r.geofips,
      county_fp3: r.countyFp3,
      location_name: r.location_name,
      year: snap.year,
      metric: snap.metric,
      metric_label: metricLabel,
      display_mode: snap.displayMode,
      population: r.population,
      sector_mt_co2e: sm,
      per_capita_mt_co2e: pc,
      map_value: mv,
    });
  }
  for (const r of snap.cityRows) {
    const pc = perCapitaForMetric(r, snap.metric);
    const sm = sectorMtForMetric(r, snap.metric);
    const mv = choroplethValueForMap(r, snap.metric, snap.displayMode);
    rows.push({
      map_panel: snap.panelLabel,
      geography_type: 'City',
      geofips: r.geofips,
      county_fp3: r.countyFp3,
      location_name: r.location_name,
      year: snap.year,
      metric: snap.metric,
      metric_label: metricLabel,
      display_mode: snap.displayMode,
      population: r.population,
      sector_mt_co2e: sm,
      per_capita_mt_co2e: pc,
      map_value: mv,
    });
  }
  return rows;
}

function CaliforniaMapLimits({ bounds }: { bounds: L.LatLngBounds }) {
  const map = useMap();
  useEffect(() => {
    const sync = () => {
      try {
        const internalMap = map as unknown as { _loaded?: boolean; _mapPane?: unknown };
        if (!internalMap._loaded || !internalMap._mapPane) return;
        if (!isUsableLatLngBounds(bounds)) return;
        map.invalidateSize(false);
        map.setMaxBounds(bounds);
        const minZ = map.getBoundsZoom(bounds, false);
        map.setMinZoom(minZ);
        if (map.getZoom() < minZ) map.setZoom(minZ);
      } catch {
        // Map may be transitioning/unmounting; ignore transient Leaflet sync errors.
      }
    };
    const onReady = () => {
      requestAnimationFrame(sync);
    };
    map.whenReady(onReady);
    map.on('resize', sync);
    const el = map.getContainer();
    const ro = new ResizeObserver(() => sync());
    if (el) ro.observe(el);
    return () => {
      map.off('resize', sync);
      ro.disconnect();
    };
  }, [map, bounds]);
  return null;
}

/** Overlay panes so city fills paint above county fills. */
function ChoroplethMapPanes() {
  const map = useMap();
  useLayoutEffect(() => {
    if (!map.getPane(PANE_COUNTIES)) {
      const p = map.createPane(PANE_COUNTIES);
      p.style.zIndex = '390';
    }
    if (!map.getPane(PANE_CITIES)) {
      const p = map.createPane(PANE_CITIES);
      p.style.zIndex = '450';
    }
  }, [map]);
  return null;
}

function formatPerCapita(v: number): string {
  if (v >= 100) return `${v.toFixed(0)} t`;
  if (v >= 10) return `${v.toFixed(1)} t`;
  return `${v.toFixed(2)} t`;
}

function formatTotalMtLegend(v: number): string {
  if (!Number.isFinite(v)) return '—';
  if (v >= 1e6) return `${(v / 1e6).toFixed(2)}M MT`;
  if (v >= 1e5) return `${(v / 1e3).toFixed(0)}k MT`;
  if (v >= 1e3) return `${(v / 1e3).toFixed(1)}k MT`;
  return `${Math.round(v).toLocaleString()} MT`;
}

/** Census county GEOID (integer) from STATEFP + COUNTYFP. */
function geofipsFromCountyFeature(p: Record<string, string | undefined> | undefined): number | null {
  if (!p || p.STATEFP !== '06' || !p.COUNTYFP) return null;
  const geoid = `06${p.COUNTYFP.padStart(3, '0')}`;
  const n = parseInt(geoid, 10);
  return Number.isFinite(n) ? n : null;
}

function geofipsFromCityFeature(p: Record<string, string | undefined> | undefined): number | null {
  const g = p?.GEOID;
  if (g == null || g === '') return null;
  return inventoryGeofipsNumeric(g);
}

function useCaliforniaCountiesGeoJson(): {
  geoJson: FeatureCollection | null;
  geoError: string | null;
} {
  const [geoJson, setGeoJson] = useState<FeatureCollection | null>(null);
  const [geoError, setGeoError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(COUNTY_GEOJSON_URL);
        if (!res.ok) throw new Error(`GeoJSON HTTP ${res.status}`);
        const data = (await res.json()) as FeatureCollection;
        if (!cancelled) {
          setGeoJson(stripPolygonHolesFromFeatureCollection(data));
          setGeoError(null);
        }
      } catch (e) {
        if (!cancelled) {
          setGeoJson(null);
          setGeoError(e instanceof Error ? e.message : 'Failed to load map boundaries');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return { geoJson, geoError };
}

/** Census place layer includes CDPs (LSAD 57, CLASSFP U1/U2); inventory City rows omit CDPs. */
const CENSUS_PLACE_LSAD_CDP = '57';

function filterCaliforniaPlaces(fc: FeatureCollection): FeatureCollection {
  return {
    type: 'FeatureCollection',
    features: fc.features.filter((f) => {
      const p = f.properties as Record<string, string | undefined> | undefined;
      if (!p) return false;
      const inCa = p.STATEFP === '06' || p.STUSPS === 'CA';
      if (!inCa) return false;
      const lsad = p.LSAD != null ? String(p.LSAD).trim() : '';
      if (lsad === CENSUS_PLACE_LSAD_CDP) return false;
      const cls = String(p.CLASSFP ?? '')
        .trim()
        .toUpperCase();
      if (cls.startsWith('U')) return false;
      return true;
    }),
  };
}

function useCaliforniaPlacesGeoJson(): FeatureCollection | null {
  const [places, setPlaces] = useState<FeatureCollection | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(PLACE_GEOJSON_URL);
        if (!res.ok) return;
        const data = (await res.json()) as FeatureCollection;
        if (!cancelled) {
          setPlaces(stripPolygonHolesFromFeatureCollection(filterCaliforniaPlaces(data)));
        }
      } catch {
        /* optional */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return places;
}

/** City fill uses parent state; stroke appears only when zoomed in (inside MapContainer). */
function CityChoroplethLayer({
  data,
  byCityGeofips,
  metric,
  displayMode,
  quintiles,
  onEachFeature,
}: {
  data: FeatureCollection;
  byCityGeofips: Map<number, CountyGhgMapRow>;
  metric: CountyGhgMapMetric;
  displayMode: CountyGhgMapDisplayMode;
  quintiles: number[] | null;
  onEachFeature: GeoJSONOptions['onEachFeature'];
}) {
  const map = useMap();
  const [zoom, setZoom] = useState(() => map.getZoom());
  useEffect(() => {
    const sync = () => setZoom(map.getZoom());
    sync();
    map.on('zoomend', sync);
    map.on('zoom', sync);
    return () => {
      map.off('zoomend', sync);
      map.off('zoom', sync);
    };
  }, [map]);

  const style = useCallback(
    (feature: Feature): L.PathOptions => {
      const q = quintiles ?? [];
      const p = feature.properties as Record<string, string | undefined> | undefined;
      const gf = geofipsFromCityFeature(p);
      const row = gf != null ? byCityGeofips.get(gf) : undefined;
      const mapVal = row ? choroplethValueForMap(row, metric, displayMode) : null;
      const qi = getQuintileIndex(mapVal, q);
      const showWhiteOutline = zoom >= CITY_CHOROPLETH_OUTLINE_MIN_ZOOM && qi != null;
      return {
        pane: PANE_CITIES,
        fillColor: getQuintileColor(qi),
        fillOpacity: 0.95,
        stroke: showWhiteOutline,
        color: '#ffffff',
        weight: showWhiteOutline ? 1 : 0,
        opacity: 1,
      };
    },
    [byCityGeofips, metric, displayMode, quintiles, zoom]
  );

  return <GeoJSON data={data} style={style as L.StyleFunction} onEachFeature={onEachFeature} />;
}

type CountyPerCapitaGhgMapPanelProps = {
  geoJson: FeatureCollection | null;
  placesGeoJson?: FeatureCollection | null;
  defaultYear?: number;
  defaultMetric?: CountyGhgMapMetric;
  panelAriaLabel: string;
  mapSyncId: string;
  mapSync: MapSyncController;
  /** Only the primary panel should auto-fit California on first load. */
  fitInitialBounds?: boolean;
  onSnapshot?: (payload: MapPanelSnapshot) => void;
};

const CountyPerCapitaGhgMapPanel: React.FC<CountyPerCapitaGhgMapPanelProps> = ({
  geoJson,
  placesGeoJson = null,
  defaultYear = PUBLICATION_GHG_SPATIAL_YEAR_MAX,
  defaultMetric = 'total',
  panelAriaLabel,
  mapSyncId,
  mapSync,
  fitInitialBounds = false,
  onSnapshot,
}) => {
  const [year, setYear] = useState(defaultYear);
  const [metric, setMetric] = useState<CountyGhgMapMetric>(defaultMetric);
  const [displayMode, setDisplayMode] = useState<CountyGhgMapDisplayMode>('per_capita');
  const [countyRows, setCountyRows] = useState<CountyGhgMapRow[]>([]);
  const [cityRows, setCityRows] = useState<CountyGhgMapRow[]>([]);
  /** Rows used only to derive quintile thresholds (normally {@link PUBLICATION_MAP_QUINTILE_REFERENCE_YEAR}). */
  const [quintileCountyRows, setQuintileCountyRows] = useState<CountyGhgMapRow[]>([]);
  const [quintileCityRows, setQuintileCityRows] = useState<CountyGhgMapRow[]>([]);
  const [quintileScaleFromReferenceYear, setQuintileScaleFromReferenceYear] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const yearOptions = useMemo(() => {
    const out: number[] = [];
    for (let y = PUBLICATION_GHG_CHART_YEAR_MIN; y <= PUBLICATION_GHG_SPATIAL_YEAR_MAX; y++) out.push(y);
    return out;
  }, []);

  const loadGhg = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    const [sel, ref] = await Promise.all([
      fetchCaliforniaCountyCityGhgForMap(year),
      fetchCaliforniaCountyCityGhgForMap(PUBLICATION_MAP_QUINTILE_REFERENCE_YEAR),
    ]);
    if (sel.error) {
      setCountyRows([]);
      setCityRows([]);
      setQuintileCountyRows([]);
      setQuintileCityRows([]);
      setQuintileScaleFromReferenceYear(false);
      setLoadError(sel.error);
    } else {
      setCountyRows(sel.countyRows);
      setCityRows(sel.cityRows);
      const refOk = !ref.error && ref.countyRows.length > 0;
      setQuintileCountyRows(refOk ? ref.countyRows : sel.countyRows);
      setQuintileCityRows(refOk ? ref.cityRows : sel.cityRows);
      setQuintileScaleFromReferenceYear(refOk);
      setLoadError(null);
    }
    setLoading(false);
  }, [year]);

  useEffect(() => {
    void loadGhg();
  }, [loadGhg]);

  useEffect(() => {
    onSnapshot?.({
      panelLabel: panelAriaLabel,
      year,
      metric,
      displayMode,
      countyRows,
      cityRows,
    });
  }, [onSnapshot, panelAriaLabel, year, metric, displayMode, countyRows, cityRows]);

  const byCountyFp = useMemo(() => {
    const m = new Map<string, CountyGhgMapRow>();
    for (const r of countyRows) {
      if (r.countyFp3) m.set(r.countyFp3, r);
    }
    return m;
  }, [countyRows]);

  const byCityGeofips = useMemo(() => {
    const m = new Map<number, CountyGhgMapRow>();
    for (const r of cityRows) {
      m.set(r.geofips, r);
    }
    return m;
  }, [cityRows]);

  const quintiles = useMemo(() => {
    const values: number[] = [];
    for (const r of quintileCountyRows) {
      const v = choroplethValueForMap(r, metric, displayMode);
      if (v != null && Number.isFinite(v) && v > 0) values.push(v);
    }
    for (const r of quintileCityRows) {
      const v = choroplethValueForMap(r, metric, displayMode);
      if (v != null && Number.isFinite(v) && v > 0) values.push(v);
    }
    return getQuintileThresholds(values);
  }, [quintileCountyRows, quintileCityRows, metric, displayMode]);

  const metricLabel = COUNTY_GHG_MAP_METRIC_OPTIONS.find((o) => o.value === metric)?.label ?? metric;

  /** TIGER includes many incorporated places with no precalc row; drawing them produced gray fills + white rims (“ghost” lines). */
  const cityPlacesForMap = useMemo((): FeatureCollection | null => {
    if (!placesGeoJson) return null;
    const features = placesGeoJson.features.filter((f) => {
      const p = f.properties as Record<string, string | undefined> | undefined;
      const gf = geofipsFromCityFeature(p);
      return gf != null && byCityGeofips.has(gf);
    });
    return { type: 'FeatureCollection', features };
  }, [placesGeoJson, byCityGeofips]);

  const hasCityPolygons = Boolean(cityPlacesForMap && cityPlacesForMap.features.length > 0);

  /** Remount choropleth layers when symbology/data changes; keep MapContainer mounted to preserve zoom/pan. */
  const choroplethLayerKey = `${year}-${metric}-${displayMode}-${quintiles?.join(',') ?? 'q'}-${
    hasCityPolygons ? 'c' : 'nc'
  }`;

  const getCountyStyle = useCallback(
    (feature: Feature): L.PathOptions => {
      const p = feature.properties as Record<string, string | undefined> | undefined;
      const gf = geofipsFromCountyFeature(p);
      if (gf == null) {
        return {
          pane: PANE_COUNTIES,
          fillColor: CHOROPLETH_NO_DATA_FILL,
          fillOpacity: 0.5,
          stroke: true,
          color: '#ffffff',
          weight: 1,
          opacity: 1,
        };
      }
      const cf = p?.COUNTYFP;
      const row = cf ? byCountyFp.get(cf) : undefined;
      const mapVal = row ? choroplethValueForMap(row, metric, displayMode) : null;
      const qi = getQuintileIndex(mapVal, quintiles ?? []);
      return {
        pane: PANE_COUNTIES,
        fillColor: getQuintileColor(qi),
        fillOpacity: 0.7,
        stroke: true,
        color: '#ffffff',
        weight: 1,
        opacity: 1,
      };
    },
    [byCountyFp, metric, displayMode, quintiles]
  );

  const onEachCounty = useCallback(
    (feature: Feature, layer: L.Layer) => {
      const p = feature.properties as Record<string, string | undefined> | undefined;
      const cf = p?.COUNTYFP;
      if (!cf || p?.STATEFP !== '06') return;
      const row = byCountyFp.get(cf);
      const pc = row ? perCapitaForMetric(row, metric) : null;
      const mapVal = row ? choroplethValueForMap(row, metric, displayMode) : null;
      const name = row?.location_name ?? `County FIPS ${cf}`;
      const pop = row?.population;
      const sectorMt = row ? sectorMtForMetric(row, metric) : null;
      const mapLine =
        displayMode === 'per_capita'
          ? `Map (per capita): ${mapVal != null ? `${formatPerCapita(mapVal)} t CO₂e / person` : '—'}`
          : `Map (total): ${mapVal != null ? `${Math.round(mapVal).toLocaleString()} MT CO₂e` : '—'}`;
      layer.bindPopup(
        `<div style="font-size:12px;line-height:1.35">
          <strong>${name}</strong> (County)<br/>
          Year: ${year}<br/>
          ${metricLabel}<br/>
          <strong>${mapLine}</strong><br/>
          Per capita: ${pc != null ? `${formatPerCapita(pc)} t CO₂e / person` : '—'}<br/>
          Population: ${pop && pop > 0 ? pop.toLocaleString() : '—'}<br/>
          Sector total (MT CO₂e): ${
            sectorMt != null && Number.isFinite(sectorMt) ? Math.round(sectorMt).toLocaleString() : '—'
          }
        </div>`
      );
    },
    [byCountyFp, displayMode, metric, metricLabel, year]
  );

  const onEachCity = useCallback(
    (feature: Feature, layer: L.Layer) => {
      const p = feature.properties as Record<string, string | undefined> | undefined;
      const gf = geofipsFromCityFeature(p);
      const label = p?.NAME || p?.NAMELSAD || 'City';
      const row = gf != null ? byCityGeofips.get(gf) : undefined;
      const pc = row ? perCapitaForMetric(row, metric) : null;
      const mapVal = row ? choroplethValueForMap(row, metric, displayMode) : null;
      const pop = row?.population;
      const sectorMt = row ? sectorMtForMetric(row, metric) : null;
      const mapLine =
        displayMode === 'per_capita'
          ? `Map (per capita): ${mapVal != null ? `${formatPerCapita(mapVal)} t CO₂e / person` : '—'}`
          : `Map (total): ${mapVal != null ? `${Math.round(mapVal).toLocaleString()} MT CO₂e` : '—'}`;
      layer.bindPopup(
        `<div style="font-size:12px;line-height:1.35">
          <strong>${label}</strong> (City)<br/>
          Year: ${year}<br/>
          ${metricLabel}<br/>
          <strong>${mapLine}</strong><br/>
          Per capita: ${pc != null ? `${formatPerCapita(pc)} t CO₂e / person` : '—'}<br/>
          Population: ${pop && pop > 0 ? pop.toLocaleString() : '—'}<br/>
          Sector total (MT CO₂e): ${
            sectorMt != null && Number.isFinite(sectorMt) ? Math.round(sectorMt).toLocaleString() : '—'
          }
        </div>`
      );
    },
    [byCityGeofips, displayMode, metric, metricLabel, year]
  );

  return (
    <div className="publication-county-ghg-map-panel">
      <div className="publication-county-ghg-controls publication-county-ghg-map-controls">
        <label className="publication-scatter-field">
          <span>Year</span>
          <select
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            disabled={loading}
            aria-label={`${panelAriaLabel}: inventory year`}
          >
            {yearOptions.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </label>
        <label className="publication-scatter-field">
          <span>Measure</span>
          <select
            value={metric}
            onChange={(e) => setMetric(e.target.value as CountyGhgMapMetric)}
            disabled={loading}
            aria-label={`${panelAriaLabel}: sector measure`}
          >
            {COUNTY_GHG_MAP_METRIC_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
        <label className="publication-scatter-field">
          <span>Map by:</span>
          <select
            value={displayMode}
            onChange={(e) => setDisplayMode(e.target.value as CountyGhgMapDisplayMode)}
            disabled={loading}
            aria-label={`${panelAriaLabel}: map by per capita or total emissions`}
          >
            <option value="per_capita">Per Capita Emissions</option>
            <option value="total_emissions">Total Emissions</option>
          </select>
        </label>
      </div>
      {loadError && <p className="publication-scatter-error">Could not load inventory: {loadError}</p>}
      {!loadError && !loading && countyRows.length === 0 && (
        <p className="publication-scatter-placeholder">
          No county rows for {year}. Run <code>npm run populate-ghg-inventory</code> for California counties.
        </p>
      )}
      {geoJson && (countyRows.length > 0 || loading) && (
        <>
          <div className="publication-county-ghg-map-wrap">
            {loading ? (
              <div className="publication-county-ghg-map-loading" role="status" aria-live="polite">
                Loading county and city GHG and population…
              </div>
            ) : null}
            {countyRows.length > 0 ? (
            <MapContainer
              center={CALIFORNIA_MAP_CENTER}
              zoom={CALIFORNIA_MAP_ZOOM}
              maxBounds={CALIFORNIA_MAP_BOUNDS}
              maxBoundsViscosity={1}
              style={{ height: '100%', width: '100%', backgroundColor: '#e8eef4' }}
              key={panelAriaLabel}
            >
              {fitInitialBounds ? (
                <FitMapBounds bounds={CALIFORNIA_MAP_BOUNDS} options={CALIFORNIA_MAP_FIT_OPTIONS} once />
              ) : null}
              <LinkedMapView mapId={mapSyncId} sync={mapSync} />
              <ChoroplethMapPanes />
              <CaliforniaMapLimits bounds={CALIFORNIA_MAP_BOUNDS} />
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              />
              <GeoJSON
                key={`counties-${choroplethLayerKey}`}
                data={geoJson}
                style={getCountyStyle as L.StyleFunction}
                onEachFeature={onEachCounty as L.GeoJSONOptions['onEachFeature']}
              />
              {hasCityPolygons && cityPlacesForMap ? (
                <CityChoroplethLayer
                  key={`cities-${choroplethLayerKey}`}
                  data={cityPlacesForMap}
                  byCityGeofips={byCityGeofips}
                  metric={metric}
                  displayMode={displayMode}
                  quintiles={quintiles}
                  onEachFeature={onEachCity as L.GeoJSONOptions['onEachFeature']}
                />
              ) : null}
            </MapContainer>
            ) : null}
          </div>
          {countyRows.length > 0 ? (
          <div className="publication-county-ghg-map-legend" aria-label="Legend">
            <div className="publication-county-ghg-map-legend-title">
              {displayMode === 'per_capita' ? 'Per Capita Emissions' : 'Total Emissions (MT CO₂e)'} — {metricLabel}{' '}
              ({year})
            </div>
            {quintiles && quintiles.length === 4 ? (
              <div className="publication-county-ghg-map-legend-swatches">
                <span className="publication-county-ghg-map-legend-item">
                  <span
                    className="publication-county-ghg-map-legend-swatch"
                    style={{ backgroundColor: CHOROPLETH_QUINTILE_COLORS[0] }}
                  />
                  <span className="publication-county-ghg-map-legend-label">
                    ≤{' '}
                    {displayMode === 'per_capita'
                      ? formatPerCapita(quintiles[0])
                      : formatTotalMtLegend(quintiles[0])}
                  </span>
                </span>
                {[1, 2, 3].map((i) => (
                  <span key={i} className="publication-county-ghg-map-legend-item">
                    <span
                      className="publication-county-ghg-map-legend-swatch"
                      style={{ backgroundColor: CHOROPLETH_QUINTILE_COLORS[i] }}
                    />
                    <span className="publication-county-ghg-map-legend-label">
                      {displayMode === 'per_capita'
                        ? `${formatPerCapita(quintiles[i - 1])} – ${formatPerCapita(quintiles[i])}`
                        : `${formatTotalMtLegend(quintiles[i - 1])} – ${formatTotalMtLegend(quintiles[i])}`}
                    </span>
                  </span>
                ))}
                <span className="publication-county-ghg-map-legend-item">
                  <span
                    className="publication-county-ghg-map-legend-swatch"
                    style={{ backgroundColor: CHOROPLETH_QUINTILE_COLORS[4] }}
                  />
                  <span className="publication-county-ghg-map-legend-label">
                    &gt;{' '}
                    {displayMode === 'per_capita'
                      ? formatPerCapita(quintiles[3])
                      : formatTotalMtLegend(quintiles[3])}
                  </span>
                </span>
              </div>
            ) : (
              <p className="publication-scatter-meta">Insufficient data for quintiles; gray = no data.</p>
            )}
            <p className="publication-county-ghg-map-legend-note">
              Breaks from{' '}
              <strong>
                {quintileScaleFromReferenceYear ? PUBLICATION_MAP_QUINTILE_REFERENCE_YEAR : year} (counties + cities)
              </strong>
              ; map values = {year}. Gray: no data.{' '}
              {year === 2005 && metric === 'waste' ? (
                <span>
                  <strong>2005 waste:</strong> uses 2006 where 2005 is zero.
                </span>
              ) : null}
            </p>
          </div>
          ) : null}
        </>
      )}
    </div>
  );
};

export const CountyPerCapitaGhgMap: React.FC = () => {
  const figureRef = useRef<HTMLDivElement>(null);
  const pngPageOverride = useContext(ChartPngPageIdOverrideContext);
  const pngPageId = pngPageOverride ?? 'statewide_analysis';
  const { register: registerPng, unregister: unregisterPng } = useChartPngRegistryActions();

  const { geoJson, geoError } = useCaliforniaCountiesGeoJson();
  const placesGeoJson = useCaliforniaPlacesGeoJson();
  const mapSync = useMapSyncController();

  const [snapLeft, setSnapLeft] = useState<MapPanelSnapshot | null>(null);
  const [snapRight, setSnapRight] = useState<MapPanelSnapshot | null>(null);
  const onSnapLeft = useCallback((p: MapPanelSnapshot) => setSnapLeft(p), []);
  const onSnapRight = useCallback((p: MapPanelSnapshot) => setSnapRight(p), []);

  const defaultYearB = useMemo(
    () => Math.max(PUBLICATION_GHG_CHART_YEAR_MIN, PUBLICATION_GHG_SPATIAL_YEAR_MAX - 1),
    []
  );

  const handleDownloadCsv = useCallback(() => {
    const combined = [...csvRecordsFromMapSnapshot(snapLeft), ...csvRecordsFromMapSnapshot(snapRight)];
    if (!combined.length) return;
    downloadDataAsCSV(combined, formatFilename('California', 'Spatial_GHG_Map_Compare'));
  }, [snapLeft, snapRight]);

  const handleDownloadPng = useCallback(() => {
    return downloadDomAsPNG(figureRef.current, formatFilename('California', 'Spatial_GHG_Map_Compare'));
  }, []);

  useEffect(() => {
    const id = PUBLICATION_ANALYSIS_CHART_IDS.spatialMap;
    const label = 'Spatial distribution map';
    registerPng(id, label, () => void handleDownloadPng(), pngPageId);
    return () => unregisterPng(id);
  }, [registerPng, unregisterPng, handleDownloadPng, pngPageId]);

  const canCsv =
    (snapLeft?.countyRows.length ?? 0) + (snapRight?.countyRows.length ?? 0) > 0;

  if (geoError) {
    return <p className="publication-county-ghg-status">Map boundaries: {geoError}</p>;
  }

  return (
    <div
      ref={figureRef}
      className="publication-county-ghg-maps-compare publication-figure-frame"
    >
      {!geoJson && (
        <p className="publication-county-ghg-status" role="status">
          Loading map boundaries…
        </p>
      )}
      <div className="publication-county-ghg-maps-grid">
        <CountyPerCapitaGhgMapPanel
          geoJson={geoJson}
          placesGeoJson={placesGeoJson}
          defaultYear={PUBLICATION_GHG_SPATIAL_YEAR_MAX}
          defaultMetric="ldv"
          panelAriaLabel="Left county map"
          mapSyncId="left"
          mapSync={mapSync}
          fitInitialBounds
          onSnapshot={onSnapLeft}
        />
        <CountyPerCapitaGhgMapPanel
          geoJson={geoJson}
          placesGeoJson={placesGeoJson}
          defaultYear={defaultYearB}
          defaultMetric="electricity"
          panelAriaLabel="Right county map"
          mapSyncId="right"
          mapSync={mapSync}
          onSnapshot={onSnapRight}
        />
      </div>
      <PublicationFigureDownloads
        onCsv={handleDownloadCsv}
        onPng={handleDownloadPng}
        csvDisabled={!canCsv}
        ariaLabel="Download spatial GHG map figure"
      />
    </div>
  );
};

export default CountyPerCapitaGhgMap;
